import {
    AdminPermissionAccessLevel,
    AdminPermissionFeature,
    AuthRoles,
    ClinicAdminPermission,
    ClinicAdminPermissionFieldsFragment,
} from '~graphql/generated/graphql'

export function getIsFeatureChecked(
    feature: AdminPermissionFeature,
    permissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
): boolean {
    return (
        permissions?.find(permissions => permissions.feature === feature)
            ?.access_level !== AdminPermissionAccessLevel.None
    )
}

export function handleUpdateFeaturePermission(
    feature: AdminPermissionFeature,
    newVal: boolean,
    permissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    setPermissions: (
        permissions: ClinicAdminPermissionFieldsFragment[],
    ) => void,
): void {
    const newPermissions = [...(permissions || [])].filter(
        permission => permission.feature !== feature,
    )

    newPermissions.push({
        feature,
        access_level: newVal
            ? AdminPermissionAccessLevel.Edit
            : AdminPermissionAccessLevel.None,
    })

    setPermissions(newPermissions)
}

export function getAllPermissionsChecked(
    label: keyof typeof FEATURES_MAP,
    permissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): boolean {
    const features = getGrantableCategoryFeatures(label, grantableFeatures)
    if (features.length === 0) return false

    return features.every(feature => {
        return permissions?.some(
            permission =>
                permission.feature === feature &&
                permission.access_level !== AdminPermissionAccessLevel.None,
        )
    })
}

export function getSomePermissionsChecked(
    label: keyof typeof FEATURES_MAP,
    permissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): boolean {
    const features = getGrantableCategoryFeatures(label, grantableFeatures)

    return features.some(feature => {
        return permissions?.some(
            permission =>
                permission.feature === feature &&
                permission.access_level !== AdminPermissionAccessLevel.None,
        )
    })
}

export function handleUpdateFeatureCategory(
    label: keyof typeof FEATURES_MAP,
    newVal: boolean,
    permissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    setPermissions: (
        permissions: ClinicAdminPermissionFieldsFragment[],
    ) => void,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): void {
    // Only toggle features the actor can grant so hidden permissions on the
    // target (ones the actor lacks) are left untouched.
    const features = getGrantableCategoryFeatures(label, grantableFeatures)

    const newPermissions = [...(permissions || [])].filter(
        permission => !features.includes(permission.feature),
    )

    newPermissions.push(
        ...features.map(feature => ({
            feature,
            access_level: newVal
                ? AdminPermissionAccessLevel.Edit
                : AdminPermissionAccessLevel.None,
        })),
    )

    setPermissions(newPermissions)
}

/**
 * Features the current user is allowed to grant. `null` means unrestricted
 * (platform admins). Callers should hide non-grantable features in the UI.
 */
export function getGrantableFeatureSet(
    actorPermissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    isAdmin?: boolean,
): ReadonlySet<AdminPermissionFeature> | null {
    if (isAdmin) return null

    return new Set(
        (actorPermissions ?? [])
            .filter(
                permission =>
                    permission.access_level !== AdminPermissionAccessLevel.None,
            )
            .map(permission => permission.feature),
    )
}

export function isFeatureGrantable(
    feature: AdminPermissionFeature,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): boolean {
    return !grantableFeatures || grantableFeatures.has(feature)
}

export function getGrantableCategoryFeatures(
    label: keyof typeof FEATURES_MAP,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): AdminPermissionFeature[] {
    return FEATURES_MAP[label].filter(feature =>
        isFeatureGrantable(feature, grantableFeatures),
    )
}

export function filterPermissionsToGrantable(
    permissions: ClinicAdminPermissionFieldsFragment[],
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): ClinicAdminPermissionFieldsFragment[] {
    if (!grantableFeatures) return permissions
    return permissions.filter(permission =>
        isFeatureGrantable(permission.feature, grantableFeatures),
    )
}

/**
 * Whether the actor can assign a preset role (Employee / Manager). Custom is
 * always allowed; individual features are filtered separately.
 */
export function canGrantRole(
    actorPermissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    role: AuthRoles,
    isClinicGroup: boolean,
    isAdmin?: boolean,
): boolean {
    if (isAdmin || role === AuthRoles.Custom) return true

    const required = getEffectivePermissions([], role, isClinicGroup).filter(
        permission =>
            permission.access_level !== AdminPermissionAccessLevel.None,
    )
    const grantable = getGrantableFeatureSet(actorPermissions, isAdmin)
    return required.every(permission =>
        isFeatureGrantable(permission.feature, grantable),
    )
}

export const GROUP_FEATURES: AdminPermissionFeature[] =
    process.env.NODE_ENV === 'production'
        ? [
              AdminPermissionFeature.GroupReporting,
              AdminPermissionFeature.Messages,
              AdminPermissionFeature.GroupTrends,
              AdminPermissionFeature.GroupWidgets,
              AdminPermissionFeature.GroupOnboarding,
              AdminPermissionFeature.SchedulingRules,
              AdminPermissionFeature.GroupUsers,
              AdminPermissionFeature.ClinicGroupInfo,
              AdminPermissionFeature.EmailTemplates,
              AdminPermissionFeature.Forms,
              AdminPermissionFeature.Reviews,
          ]
        : [
              AdminPermissionFeature.GroupReporting,
              AdminPermissionFeature.Messages,
              AdminPermissionFeature.GroupTrends,
              AdminPermissionFeature.GroupInitiatives,
              AdminPermissionFeature.GroupKpIs,
              AdminPermissionFeature.GroupWidgets,
              AdminPermissionFeature.GroupOnboarding,
              AdminPermissionFeature.SchedulingRules,
              AdminPermissionFeature.GroupUsers,
              AdminPermissionFeature.ClinicGroupInfo,
              AdminPermissionFeature.EmailTemplates,
              AdminPermissionFeature.Forms,
              AdminPermissionFeature.Reviews,
          ]

export const FEATURES_MAP: Record<string, AdminPermissionFeature[]> = {
    General: [
        AdminPermissionFeature.Messages,
        AdminPermissionFeature.TeamChat,
        AdminPermissionFeature.Whiteboard,
        AdminPermissionFeature.DailyHuddle,
        AdminPermissionFeature.PendingActions,
    ],
    Analytics: [
        AdminPermissionFeature.Goals,
        AdminPermissionFeature.Reporting,
        AdminPermissionFeature.Trends,
    ],
    Booking: [
        AdminPermissionFeature.Clinicians,
        AdminPermissionFeature.Payments,
        AdminPermissionFeature.Rooms,
        AdminPermissionFeature.SchedulingRules,
        AdminPermissionFeature.Services,
        AdminPermissionFeature.Widgets,
    ],
    Engagement: [
        AdminPermissionFeature.ClientSegments,
        AdminPermissionFeature.BulkMessages,
        AdminPermissionFeature.Campaigns,
        AdminPermissionFeature.EmailTemplates,
        AdminPermissionFeature.Forms,
        AdminPermissionFeature.Loyalty,
        AdminPermissionFeature.MessageLogs,
        AdminPermissionFeature.MessageTemplates,
        AdminPermissionFeature.Notifications,
        AdminPermissionFeature.Reviews,
    ],
    Clinic: [
        AdminPermissionFeature.Appointments,
        AdminPermissionFeature.ClinicInfo,
        AdminPermissionFeature.Users,
    ],
    Settings: [
        AdminPermissionFeature.GeneralSettings,
        AdminPermissionFeature.BookingSettings,
        AdminPermissionFeature.EngagementSettings,
        AdminPermissionFeature.SyncSettings,
    ],
}

export const FEATURE_LABEL_MAP: Partial<
    Record<AdminPermissionFeature, string>
> = {
    [AdminPermissionFeature.Messages]: 'Inbox',
    [AdminPermissionFeature.ClientSegments]: 'Audiences',
}

export const FEATURE_DESCRIPTION_MAP: Record<
    AdminPermissionFeature,
    string | undefined
> = {
    [AdminPermissionFeature.DailyHuddle]:
        'Allows access to view Daily Huddle and send messages from Daily Huddle action items.',
    [AdminPermissionFeature.PendingActions]:
        'Allows access to view and manage Pending Actions, including dismissing and responding.',
    [AdminPermissionFeature.Messages]:
        'Allows access to view and manage messages, including sending and responding.',
    [AdminPermissionFeature.TeamChat]:
        'Allows access to view and participate in internal Team Chat conversations.',
    [AdminPermissionFeature.Whiteboard]:
        'Allows access to view and manage the Whiteboard, including moving and updating cards.',
    [AdminPermissionFeature.Appointments]:
        'Allows access to view all appointments on the appointments view.',
    [AdminPermissionFeature.Goals]:
        'Allows access to view and manage goals, including setting and tracking progress.',
    [AdminPermissionFeature.Reporting]:
        'Allows access to view and manage reports, including generating and exporting reports.',
    [AdminPermissionFeature.Trends]:
        'Allows access to view and manage trends, including analyzing and visualizing data.',
    [AdminPermissionFeature.Clinicians]:
        'Allows access to view and manage clinicians, including updaing rules and available hours.',
    [AdminPermissionFeature.Rooms]:
        'Allows access to view and manage rooms, including updating rules and available hours.',
    [AdminPermissionFeature.Payments]:
        'Allows access to view all payments processed through Chckvet.',
    [AdminPermissionFeature.SchedulingRules]:
        'Allows access to view and manage scheduling rules, including updating and deleting rules.',
    [AdminPermissionFeature.Services]:
        'Allows access to view and manage services, including creating and updating services.',
    [AdminPermissionFeature.Widgets]:
        'Allows access to view and manage widgets, including creating and updating widgets.',
    [AdminPermissionFeature.BulkMessages]:
        'Allows access to view and manage bulk messages, including creating and sending bulk messages.',
    [AdminPermissionFeature.Campaigns]:
        'Allows access to view and manage campaigns, including creating and updating campaigns.',
    [AdminPermissionFeature.ClientSegments]:
        'Allows access to view and manage audiences, including creating and updating audiences.',
    [AdminPermissionFeature.EmailTemplates]:
        'Allows access to view and manage email templates, including creating, updating, and deleting templates.',
    [AdminPermissionFeature.Forms]:
        'Allows access to view and manage forms, including creating and updating forms.',
    [AdminPermissionFeature.Loyalty]:
        'Allows access to view and manage loyalty programs, including accepting redemptions and creating new rewards.',
    [AdminPermissionFeature.MessageLogs]:
        'Allows access to view all message logs for notifications send through Chckvet.',
    [AdminPermissionFeature.MessageTemplates]:
        'Allows access to view and manage message templates, including creating, updating, and deleting templates.',
    [AdminPermissionFeature.Notifications]:
        'Allows access to view and manage notification templates, including creating and updating notifications templates.',
    [AdminPermissionFeature.Reviews]:
        'Allows access to view all reviews submitted through Chckvet.',
    [AdminPermissionFeature.ClinicInfo]:
        'Allows access to view and manage clinic information, including updating clinic details.',
    [AdminPermissionFeature.Users]:
        'Allows access to view and manage users, including creating and updating users.',
    [AdminPermissionFeature.GeneralSettings]:
        'Allows access to view and manage all general settings.',
    [AdminPermissionFeature.BookingSettings]:
        'Allows access to view and manage all booking settings.',
    [AdminPermissionFeature.EngagementSettings]:
        'Allows access to view and manage all engagement settings.',
    [AdminPermissionFeature.SyncSettings]:
        'Allows access to view and manage all sync settings.',
    [AdminPermissionFeature.GroupReporting]:
        'Allows access to view and manage group reporting, including generating and exporting reports.',
    [AdminPermissionFeature.GroupTrends]:
        'Allows access to view and manage group trends, including analyzing and visualizing data.',
    [AdminPermissionFeature.GroupInitiatives]:
        'Allows access to view and manage group initiatives, including creating and updating initiatives.',
    [AdminPermissionFeature.GroupKpIs]:
        'Allows access to view and manage group KPIs, including creating and updating KPIs.',
    [AdminPermissionFeature.GroupOnboarding]:
        'Allows access to the group onboarding report, including updating onboarding steps and per-clinic progress.',
    [AdminPermissionFeature.GroupWidgets]:
        'Allows access to view and manage group widgets, including creating and updating widgets.',
    [AdminPermissionFeature.GroupUsers]:
        'Allows access to view and manage group users, including creating and updating users.',
    [AdminPermissionFeature.ClinicGroupInfo]:
        'Allows access to view and manage clinic group information, including updating clinic group details.',
}

export const DEFAULT_CLINIC_ADMIN_PERMISSIONS: ClinicAdminPermission[] = [
    // General permissions
    {
        feature: AdminPermissionFeature.Messages,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.TeamChat,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Whiteboard,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.DailyHuddle,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.PendingActions,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    // Analytics permissions
    {
        feature: AdminPermissionFeature.Goals,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Reporting,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Trends,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    // Booking permissions
    {
        feature: AdminPermissionFeature.Widgets,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Clinicians,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Payments,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Rooms,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.SchedulingRules,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Services,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    // Engagement permissions
    {
        feature: AdminPermissionFeature.ClientSegments,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.BulkMessages,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Campaigns,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.EmailTemplates,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Forms,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Loyalty,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.MessageLogs,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.MessageTemplates,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Notifications,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Reviews,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    // Clinic permissions
    {
        feature: AdminPermissionFeature.Appointments,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.ClinicInfo,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Users,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    // Settings permissions
    {
        feature: AdminPermissionFeature.GeneralSettings,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.BookingSettings,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.EngagementSettings,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.SyncSettings,
        access_level: AdminPermissionAccessLevel.Edit,
    },
]

export const DEFAULT_CLINIC_GROUP_USER_PERMISSIONS: ClinicAdminPermission[] = [
    {
        feature: AdminPermissionFeature.GroupReporting,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.GroupTrends,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.GroupInitiatives,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.GroupKpIs,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.GroupWidgets,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.GroupUsers,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.ClinicGroupInfo,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.EmailTemplates,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Forms,
        access_level: AdminPermissionAccessLevel.Edit,
    },
    {
        feature: AdminPermissionFeature.Reviews,
        access_level: AdminPermissionAccessLevel.Edit,
    },
]

// ============================================================================
// Role default permissions — read-side fallback for missing JSON entries
// ============================================================================
//
// The source of truth for what each role gets by default is the server, at
// `server/src/db/helpers/clinic-admin.helpers.ts`. The maps below MUST mirror
// those defaults exactly. Keep them in sync when adding/changing features.
//
// Why mirror them here at all: a clinic_admin's `permissions` JSON column is
// written once at creation (and only re-written on manual edit). When a new
// `AdminPermissionFeature` is added to the role defaults later, every existing
// row's JSON still reflects the OLD defaults — the new feature simply has no
// entry. Because `getHasPermissions` reads via `.some()`, a missing entry
// behaves identically to `access_level: None`, silently denying access.
//
// Server-side `resolveEffectiveAdminPermissions` is now the primary source of
// truth (Manager/Employee → DEFAULT_* maps; Custom+template → custom role).
// `getEffectivePermissions` remains as a client-side safety net for sparse
// legacy payloads: it overlays role defaults for features the list omits.
// Explicit `None` entries are preserved. Custom roles get no overlay.

type FeatureDefaultsMap = Partial<
    Record<AdminPermissionFeature, AdminPermissionAccessLevel>
>

const CLINIC_EMPLOYEE_DEFAULTS: FeatureDefaultsMap = {
    [AdminPermissionFeature.DailyHuddle]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.PendingActions]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.Messages]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.TeamChat]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Whiteboard]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.Appointments]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.Payments]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.Loyalty]: AdminPermissionAccessLevel.View,
    [AdminPermissionFeature.Reviews]: AdminPermissionAccessLevel.View,
}

const CLINIC_MANAGER_DEFAULTS: FeatureDefaultsMap = {
    [AdminPermissionFeature.DailyHuddle]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.PendingActions]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Messages]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.TeamChat]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Whiteboard]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Appointments]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Goals]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Reporting]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Trends]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Clinicians]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Rooms]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Payments]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.SchedulingRules]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Services]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Widgets]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.BulkMessages]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Campaigns]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.ClientSegments]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.EmailTemplates]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Forms]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Loyalty]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.MessageLogs]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.MessageTemplates]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Notifications]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Reviews]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.ClinicInfo]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Users]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GeneralSettings]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.BookingSettings]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.EngagementSettings]:
        AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.SyncSettings]: AdminPermissionAccessLevel.Edit,
}

const CLINIC_GROUP_EMPLOYEE_DEFAULTS: FeatureDefaultsMap = {
    [AdminPermissionFeature.GroupReporting]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Messages]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupTrends]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupInitiatives]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupKpIs]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupWidgets]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.SchedulingRules]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Forms]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Reviews]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.TeamChat]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Whiteboard]: AdminPermissionAccessLevel.Edit,
}

const CLINIC_GROUP_MANAGER_DEFAULTS: FeatureDefaultsMap = {
    [AdminPermissionFeature.GroupReporting]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Messages]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupTrends]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupInitiatives]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupKpIs]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupWidgets]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupOnboarding]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.SchedulingRules]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.GroupUsers]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.ClinicGroupInfo]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.EmailTemplates]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Forms]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Reviews]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.TeamChat]: AdminPermissionAccessLevel.Edit,
    [AdminPermissionFeature.Whiteboard]: AdminPermissionAccessLevel.Edit,
}

function getDefaultsForRole(
    role: AuthRoles | null | undefined,
    isClinicGroup: boolean,
): FeatureDefaultsMap | null {
    if (isClinicGroup) {
        switch (role) {
            case AuthRoles.User:
            case AuthRoles.ClinicEmployee:
                return CLINIC_GROUP_EMPLOYEE_DEFAULTS
            case AuthRoles.ClinicManager:
            case AuthRoles.SuperAdmin:
                return CLINIC_GROUP_MANAGER_DEFAULTS
            default:
                return null
        }
    }

    switch (role) {
        case AuthRoles.User:
        case AuthRoles.ClinicEmployee:
            return CLINIC_EMPLOYEE_DEFAULTS
        case AuthRoles.ClinicManager:
        case AuthRoles.SuperAdmin:
            return CLINIC_MANAGER_DEFAULTS
        default:
            return null
    }
}

/**
 * Returns the user's effective permissions, with role defaults overlaid for
 * any feature missing from the JSON. Existing entries (including explicit
 * `None` revocations) are passed through unchanged. For roles without
 * server-side defaults (e.g. `Custom`), the input is returned as-is.
 *
 * @param permissions The raw `permissions` array from the clinic_admin or
 *                    clinic_group_user row.
 * @param role        The user's role at the current clinic/group.
 * @param isClinicGroup True when sourcing permissions from a
 *                    clinic_group_user row; selects the group-level defaults.
 */
export function getEffectivePermissions(
    permissions: ClinicAdminPermissionFieldsFragment[] | null | undefined,
    role: AuthRoles | null | undefined,
    isClinicGroup: boolean,
): ClinicAdminPermissionFieldsFragment[] {
    const existing = permissions ?? []
    const defaults = getDefaultsForRole(role, isClinicGroup)
    if (!defaults) return existing

    const presentFeatures = new Set(existing.map(p => p.feature))
    const missing: ClinicAdminPermissionFieldsFragment[] = []
    for (const [feature, accessLevel] of Object.entries(defaults) as [
        AdminPermissionFeature,
        AdminPermissionAccessLevel,
    ][]) {
        if (!presentFeatures.has(feature)) {
            missing.push({ feature, access_level: accessLevel })
        }
    }

    return missing.length === 0 ? existing : [...existing, ...missing]
}

export function getFeatureDisplayName(
    feature: AdminPermissionFeature,
): string {
    if (FEATURE_LABEL_MAP[feature]) return FEATURE_LABEL_MAP[feature]!
    // Mirror getCleanEnum without importing utils (avoids circular deps).
    return feature.replace(/_/g, ' ').replace(/[A-Z]/g, ' $&').trim()
}

/**
 * Enabled features in `requested` that the actor cannot grant. Returns an
 * empty list when the actor is unrestricted (`grantableFeatures === null`).
 */
export function getExcludedFeatures(
    requestedPermissions:
        | ClinicAdminPermissionFieldsFragment[]
        | null
        | undefined,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): AdminPermissionFeature[] {
    if (!grantableFeatures) return []

    const excluded = new Set<AdminPermissionFeature>()
    for (const permission of requestedPermissions ?? []) {
        if (
            permission.access_level !== AdminPermissionAccessLevel.None &&
            !grantableFeatures.has(permission.feature)
        ) {
            excluded.add(permission.feature)
        }
    }
    return [...excluded]
}

/**
 * Features a preset role (Employee / Manager) would enable that the actor
 * cannot grant.
 */
export function getExcludedFeaturesForRole(
    role: AuthRoles,
    isClinicGroup: boolean,
    grantableFeatures?: ReadonlySet<AdminPermissionFeature> | null,
): AdminPermissionFeature[] {
    if (!grantableFeatures || role === AuthRoles.Custom) return []

    return getExcludedFeatures(
        getEffectivePermissions([], role, isClinicGroup),
        grantableFeatures,
    )
}
